import { useEffect, useState } from "react";
import { ImagePlus, Save, X } from "lucide-react";
import { toast } from "sonner";
import { adminGetContent, adminPutContent, adminUpload } from "@/lib/api";
import { useSite } from "@/lib/site";

const SECTIONS = [
  {
    id: "general", label: "General",
    fields: [
      { key: "brand_name", label: "Brand name" },
      { key: "tagline", label: "Tagline" },
      { key: "contact_email", label: "Contact email" },
    ],
  },
  {
    id: "hero", label: "Hero",
    fields: [
      { key: "headline", label: "Headline" },
      { key: "subheading", label: "Subheading", textarea: true },
      { key: "cta_label", label: "Button label" },
      { key: "cta_href", label: "Button link (e.g. #campaign or /shop)" },
      { key: "bg_image", label: "Background image", image: true },
    ],
  },
  {
    id: "campaign", label: "Campaign",
    fields: [
      { key: "title", label: "Title" },
      { key: "subtitle", label: "Subtitle", textarea: true },
      { key: "cta_label", label: "Back button label" },
    ],
  },
  {
    id: "problems", label: "Problems",
    fields: [
      { key: "title", label: "Title" },
      { key: "buyer_text", label: "Buyer card text", textarea: true },
      { key: "manufacturer_text", label: "Manufacturer card text", textarea: true },
      { key: "designer_text", label: "Designer card text", textarea: true },
    ],
  },
  {
    id: "shift", label: "The Shift",
    fields: [
      { key: "title", label: "Title" },
      { key: "body", label: "Body", textarea: true },
    ],
  },
  {
    id: "services", label: "Services",
    fields: [
      { key: "title", label: "Title" },
      { key: "subtitle", label: "Subtitle", textarea: true },
      { key: "cta_label", label: "Button label" },
      { key: "cta_href", label: "Button link" },
    ],
  },
  {
    id: "booking", label: "Booking",
    fields: [{ key: "title", label: "Title" }],
  },
  {
    id: "footer", label: "Footer",
    fields: [
      { key: "tagline", label: "Tagline", textarea: true },
      { key: "instagram_url", label: "Instagram URL" },
      { key: "facebook_url", label: "Facebook URL" },
      { key: "email", label: "Email" },
    ],
  },
  {
    id: "modal", label: "Welcome modal",
    fields: [
      { key: "title", label: "Title" },
      { key: "body", label: "Body", textarea: true },
      { key: "email", label: "Contact email" },
    ],
  },
];

function ImageField({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    try {
      const r = await adminUpload(file);
      onChange(r.url);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-3 mt-1">
      {value ? <img src={value} alt="" className="h-14 w-14 rounded-lg object-cover bg-[#F4F1EC]" /> : (
        <div className="h-14 w-14 rounded-lg bg-[#F4F1EC] grid place-items-center text-[#1F3864]/30"><ImagePlus size={18} /></div>
      )}
      <label className="cursor-pointer text-sm font-bold text-[#1F3864] border border-[#1F3864]/20 rounded-lg px-3 py-2 hover:bg-[#F4F1EC]">
        {busy ? "Uploading…" : "Upload image"}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      </label>
      {value && <button onClick={() => onChange("")} className="text-[#FF8473]"><X size={16} /></button>}
    </div>
  );
}

export default function ContentAdmin() {
  const { refresh } = useSite();
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminGetContent().then(setContent).catch(() => toast.error("Failed to load content")); }, []);

  if (!content) return <div className="text-[#1F3864]/50 animate-pulse">Loading content…</div>;

  const setField = (section, key, value) =>
    setContent((c) => ({ ...c, [section]: { ...(c[section] || {}), [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await adminPutContent(content);
      await refresh();
      toast.success("Content saved — live on the site");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="admin-content">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl text-[#1F3864] mb-1">Content</h1>
          <p className="text-[#1F3864]/60">Edit every text and image on the landing page.</p>
        </div>
        <button data-testid="save-content-btn" onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 bg-[#1F3864] text-[#D7E85C] font-bold px-5 py-3 rounded-full hover:bg-[#16294B] disabled:opacity-50">
          <Save size={16} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="space-y-4">
        {SECTIONS.map((s) => (
          <details key={s.id} data-testid={`content-section-${s.id}`} className="bg-white rounded-2xl border border-[#1F3864]/10 group" open={s.id === "general"}>
            <summary className="cursor-pointer px-5 py-4 font-bold text-[#1F3864] list-none flex items-center justify-between">
              {s.label}
              <span className="text-[#1F3864]/40 text-xs group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <div className="px-5 pb-5 space-y-4 border-t border-[#1F3864]/10 pt-4">
              {s.fields.map((f) => (
                <div key={f.key}>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">{f.label}</label>
                  {f.image ? (
                    <ImageField value={content[s.id]?.[f.key] || ""} onChange={(v) => setField(s.id, f.key, v)} />
                  ) : f.textarea ? (
                    <textarea data-testid={`field-${s.id}-${f.key}`} value={content[s.id]?.[f.key] || ""}
                      onChange={(e) => setField(s.id, f.key, e.target.value)} rows={3}
                      className="mt-1 w-full border border-[#1F3864]/20 rounded-lg px-3 py-2.5 outline-none focus:border-[#1F3864] text-sm" />
                  ) : (
                    <input data-testid={`field-${s.id}-${f.key}`} value={content[s.id]?.[f.key] || ""}
                      onChange={(e) => setField(s.id, f.key, e.target.value)}
                      className="mt-1 w-full border border-[#1F3864]/20 rounded-lg px-3 py-2.5 outline-none focus:border-[#1F3864] text-sm" />
                  )}
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}
